import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FeatureEntity } from '../../../types';
import {
  createAdminLabsFeature,
  updateAdminLabsFeature,
  UserKeys,
} from '../../api/users-api';
import { useStore } from '../state/zustand-store';
import { handleErrors } from '../../utils/errors';
import SharedDialog from '../../shared/shared-dialog';
import { Messages } from '../../utils/message-constants';
import { useUserSettings } from '../../utils/use-user-settings';

import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';

export interface LabsFeatureDialogProps {
  open: boolean;
  handleClose: () => void;
  featureToEdit?: FeatureEntity;
}
export default function LabsFeatureDialog({
  open,
  handleClose,
  featureToEdit,
}: LabsFeatureDialogProps) {
  const queryClient = useQueryClient();
  const { loggedInUser } = useUserSettings();
  const { setSnackbar } = useStore();
  const isEditMode = !!featureToEdit;
  const defaultFormValues: FeatureEntity = {
    featureName: '',
    featureString: '',
    description: '',
    hidden: false,
  };
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isValid },
  } = useForm<FeatureEntity>({
    values: featureToEdit || defaultFormValues,
    mode: 'onChange',
  });

  const { mutate, isPending } = useMutation({
    mutationFn: (feature: FeatureEntity) => {
      if (isEditMode) {
        return updateAdminLabsFeature(feature);
      }
      return createAdminLabsFeature(feature);
    },
    onSuccess: (_data, newFeature) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: isEditMode
          ? Messages.Success.Updated('Feature')
          : Messages.Success.Created('Feature'),
      });
      queryClient.setQueryData<FeatureEntity[]>(
        [UserKeys.AdminLabsPage],
        (oldData) => {
          const now = new Date().getTime();
          if (isEditMode) {
            return oldData?.map((feature) =>
              feature.featureString === featureToEdit?.featureString
                ? {
                    ...newFeature,
                    updatedBy: loggedInUser,
                    updatedDate: now,
                  }
                : feature
            );
          }
          const newFeatureWithMeta = {
            ...newFeature,
            creator: loggedInUser,
            createdDate: now,
            updatedBy: '',
            updatedDate: 0,
          };
          return [...(oldData || []), newFeatureWithMeta];
        }
      );
      handleClose();
      reset();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: isEditMode
          ? Messages.Errors.Network.Updating('Feature')
          : Messages.Errors.Network.Creating('Feature'),
      });
    },
  });

  const processSubmit: SubmitHandler<FeatureEntity> = (data) => {
    const featurePayload: FeatureEntity = {
      ...featureToEdit,
      ...data,
    };
    mutate(featurePayload);
  };

  return (
    <SharedDialog
      open={open}
      title={isEditMode ? 'Edit Labs Feature' : 'Create Labs Feature'}
      handleClose={() => {
        reset();
        handleClose();
      }}
      maxWidth="sm"
      dialogContent={
        <Stack spacing={1} sx={{ mt: 1 }}>
          <Controller
            name="featureString"
            control={control}
            rules={{
              required: 'Feature String is required.',
              validate: (value) => {
                if (isEditMode && value === featureToEdit?.featureString) {
                  return true;
                }
                const features = queryClient.getQueryData<FeatureEntity[]>([
                  UserKeys.AdminLabsPage,
                ]);
                if (
                  features?.some((feature) => feature.featureString === value)
                ) {
                  return 'Feature already exists.';
                }
                return true;
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Feature"
                fullWidth
                required
                error={!!errors.featureString}
                helperText={errors.featureString?.message || ' '}
              />
            )}
          />
          <Controller
            name="featureName"
            control={control}
            rules={{ required: 'Feature Label is required.' }}
            render={({ field }) => (
              <TextField
                {...field}
                autoFocus
                label="Feature Label"
                fullWidth
                required
                error={!!errors.featureName}
                helperText={errors.featureName?.message || ' '}
              />
            )}
          />
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value || ''}
                label="Label Description"
                multiline
                rows={4}
                fullWidth
              />
            )}
          />
          <Controller
            name="hidden"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Switch {...field} checked={!!field.value} />}
                label="Hidden Feature"
              />
            )}
          />
        </Stack>
      }
      noCancel
      primaryButton={{
        loading: isPending,
        action: handleSubmit(processSubmit),
        disabled: isPending || !isDirty || !isValid,
        title: isEditMode ? 'Save Changes' : 'Add Feature',
      }}
    />
  );
}
